/**
 * Firebase Configuration for Web Push Notifications
 * 
 * IMPORTANT: Add the following environment variables to your .env file:
 * - REACT_APP_FIREBASE_API_KEY
 * - REACT_APP_FIREBASE_AUTH_DOMAIN
 * - REACT_APP_FIREBASE_PROJECT_ID
 * - REACT_APP_FIREBASE_STORAGE_BUCKET
 * - REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 * - REACT_APP_FIREBASE_APP_ID
 * - REACT_APP_FIREBASE_VAPID_KEY
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'gk4i-web-push.firebaseapp.com',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'gk4i-web-push',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'gk4i-web-push.firebasestorage.app',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '172052590309',
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// VAPID key for web push (generate in Firebase Console → Cloud Messaging)
const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Initialize Firebase
let app = null;
let messaging = null;

/**
 * Initialize Firebase app and messaging
 * @returns {Promise<boolean>} - True if initialization succeeded
 */
export const initializeFirebase = async () => {
    try {
        // Check if messaging is supported in this browser
        const supported = await isSupported();
        if (!supported) {
            console.warn('Firebase Messaging is not supported in this browser');
            return false;
        }

        // Debug: Log config status
        console.log('[Firebase] Config check:', {
            hasApiKey: !!firebaseConfig.apiKey,
            hasAppId: !!firebaseConfig.appId,
            hasVapidKey: !!vapidKey,
            projectId: firebaseConfig.projectId
        });

        // Check for required config
        if (!firebaseConfig.apiKey || !firebaseConfig.appId) {
            console.error('Firebase configuration is incomplete. Missing:', {
                apiKey: !firebaseConfig.apiKey ? 'REACT_APP_FIREBASE_API_KEY' : 'OK',
                appId: !firebaseConfig.appId ? 'REACT_APP_FIREBASE_APP_ID' : 'OK'
            });
            return false;
        }

        if (!vapidKey) {
            console.error('Firebase VAPID key missing. Add REACT_APP_FIREBASE_VAPID_KEY to environment');
            return false;
        }

        // Initialize app if not already initialized
        if (!app) {
            app = initializeApp(firebaseConfig);
            console.log('[Firebase] App initialized');
        }

        // Initialize messaging
        messaging = getMessaging(app);
        console.log('[Firebase] Messaging initialized');

        return true;
    } catch (error) {
        console.error('Error initializing Firebase:', error);
        return false;
    }
};

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} - FCM token or null if failed
 */
export const requestNotificationPermission = async () => {
    try {
        // Check if Firebase is initialized
        if (!messaging) {
            const initialized = await initializeFirebase();
            if (!initialized) {
                return null;
            }
        }

        // Check VAPID key
        if (!vapidKey) {
            console.warn('VAPID key not configured. Add REACT_APP_FIREBASE_VAPID_KEY to .env');
            return null;
        }

        // Request permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.log('Notification permission denied');
            return null;
        }

        // Use the existing service worker registration (sw.js) instead of
        // letting Firebase register its own, which conflicts with the PWA SW.
        const swRegistration = await navigator.serviceWorker.ready;

        // Get FCM token
        const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });

        if (token) {
            console.log('FCM Token obtained:', token.substring(0, 20) + '...');
            return token;
        } else {
            console.warn('No FCM token available');
            return null;
        }
    } catch (error) {
        console.error('Error getting notification permission:', error);
        return null;
    }
};

/**
 * Set up foreground message handler
 * @param {Function} callback - Function to call when message received
 * @returns {Function|null} - Unsubscribe function or null
 */
export const onForegroundMessage = (callback) => {
    if (!messaging) {
        console.warn('Firebase Messaging not initialized');
        return null;
    }

    return onMessage(messaging, (payload) => {
        console.log('Foreground message received:', payload);
        callback(payload);
    });
};

/**
 * Get current FCM token without requesting permission
 * @returns {Promise<string|null>} - FCM token or null
 */
export const getCurrentToken = async () => {
    try {
        if (!messaging) {
            const initialized = await initializeFirebase();
            if (!initialized) return null;
        }

        if (!vapidKey) return null;

        // Check if permission already granted
        if (Notification.permission !== 'granted') {
            return null;
        }

        const swRegistration = await navigator.serviceWorker.ready;
        const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
        return token || null;
    } catch (error) {
        console.error('Error getting current token:', error);
        return null;
    }
};

export { app, messaging };
